import * as i1$1 from '@angular/cdk/bidi';
import { BidiModule } from '@angular/cdk/bidi';
import * as i0 from '@angular/core';
import { Injectable, Directive, Inject, Input, NgModule } from '@angular/core';
import * as i1 from '@ngbracket/ngx-layout/core';
import { StyleBuilder, BaseDirective2, ɵmultiply as _multiply, LAYOUT_CONFIG, validateBasis, CoreModule } from '@ngbracket/ngx-layout/core';
import { isFlowHorizontal, extendObject, LAYOUT_VALUES, buildLayoutCSS } from '@ngbracket/ngx-layout/_private-utils';
import { takeUntil } from 'rxjs/operators';
import { Subject } from 'rxjs';

/**
 * @license
 * Copyright Google LLC All Rights Reserved.
 *
 * Use of this source code is governed by an MIT-style license that can be
 * found in the LICENSE file at https://angular.io/license
 */
let FlexAlignStyleBuilder = /*#__PURE__*/(() => {
  class FlexAlignStyleBuilder extends StyleBuilder {
    buildStyles(input) {
      input = input || 'stretch';
      const styles = {};
      // Cross-axis
      switch (input) {
        case 'start':
          styles['align-self'] = 'flex-start';
          break;
        case 'end':
          styles['align-self'] = 'flex-end';
          break;
        default:
          styles['align-self'] = input;
          break;
      }
      return styles;
    }
    static {
      this.ɵfac = /* @__PURE__ */(() => {
        let ɵFlexAlignStyleBuilder_BaseFactory;
        return function FlexAlignStyleBuilder_Factory(__ngFactoryType__) {
          return (ɵFlexAlignStyleBuilder_BaseFactory || (ɵFlexAlignStyleBuilder_BaseFactory = i0.ɵɵgetInheritedFactory(FlexAlignStyleBuilder)))(__ngFactoryType__ || FlexAlignStyleBuilder);
        };
      })();
    }
    static {
      this.ɵprov = /* @__PURE__ */i0.ɵɵdefineInjectable({
        token: FlexAlignStyleBuilder,
        factory: FlexAlignStyleBuilder.ɵfac,
        providedIn: 'root'
      });
    }
  }
  return FlexAlignStyleBuilder;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();
const inputs$6 = ['fxFlexAlign', 'fxFlexAlign.xs', 'fxFlexAlign.sm', 'fxFlexAlign.md', 'fxFlexAlign.lg', 'fxFlexAlign.xl', 'fxFlexAlign.lt-sm', 'fxFlexAlign.lt-md', 'fxFlexAlign.lt-lg', 'fxFlexAlign.lt-xl', 'fxFlexAlign.gt-xs', 'fxFlexAlign.gt-sm', 'fxFlexAlign.gt-md', 'fxFlexAlign.gt-lg'];
const selector$6 = `
  [fxFlexAlign], [fxFlexAlign.xs], [fxFlexAlign.sm], [fxFlexAlign.md],
  [fxFlexAlign.lg], [fxFlexAlign.xl], [fxFlexAlign.lt-sm], [fxFlexAlign.lt-md],
  [fxFlexAlign.lt-lg], [fxFlexAlign.lt-xl], [fxFlexAlign.gt-xs], [fxFlexAlign.gt-sm],
  [fxFlexAlign.gt-md], [fxFlexAlign.gt-lg]
`;
/**
 * 'flex-align' flexbox styling directive
 * Allows element-specific overrides for cross-axis alignments in a layout container
 * @see https://css-tricks.com/almanac/properties/a/align-self/
 */
let FlexAlignDirective = /*#__PURE__*/(() => {
  class FlexAlignDirective extends BaseDirective2 {
    constructor(elRef, styleUtils, styleBuilder, marshal) {
      super(elRef, styleBuilder, styleUtils, marshal);
      this.DIRECTIVE_KEY = 'flex-align';
      this.styleCache = flexAlignCache;
      this.init();
    }
    static {
      this.ɵfac = function FlexAlignDirective_Factory(__ngFactoryType__) {
        return new (__ngFactoryType__ || FlexAlignDirective)(i0.ɵɵdirectiveInject(i0.ElementRef), i0.ɵɵdirectiveInject(i1.StyleUtils), i0.ɵɵdirectiveInject(FlexAlignStyleBuilder), i0.ɵɵdirectiveInject(i1.MediaMarshaller));
      };
    }
    static {
      this.ɵdir = /* @__PURE__ */i0.ɵɵdefineDirective({
        type: FlexAlignDirective,
        features: [i0.ɵɵInheritDefinitionFeature]
      });
    }
  }
  return FlexAlignDirective;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();
const flexAlignCache = new Map();
let DefaultFlexAlignDirective = /*#__PURE__*/(() => {
  class DefaultFlexAlignDirective extends FlexAlignDirective {
    constructor() {
      super(...arguments);
      this.inputs = inputs$6;
    }
    static {
      this.ɵfac = /* @__PURE__ */(() => {
        let ɵDefaultFlexAlignDirective_BaseFactory;
        return function DefaultFlexAlignDirective_Factory(__ngFactoryType__) {
          return (ɵDefaultFlexAlignDirective_BaseFactory || (ɵDefaultFlexAlignDirective_BaseFactory = i0.ɵɵgetInheritedFactory(DefaultFlexAlignDirective)))(__ngFactoryType__ || DefaultFlexAlignDirective);
        };
      })();
    }
    static {
      this.ɵdir = /* @__PURE__ */i0.ɵɵdefineDirective({
        type: DefaultFlexAlignDirective,
        selectors: [["", "fxFlexAlign", ""], ["", "fxFlexAlign.xs", ""], ["", "fxFlexAlign.sm", ""], ["", "fxFlexAlign.md", ""], ["", "fxFlexAlign.lg", ""], ["", "fxFlexAlign.xl", ""], ["", "fxFlexAlign.lt-sm", ""], ["", "fxFlexAlign.lt-md", ""], ["", "fxFlexAlign.lt-lg", ""], ["", "fxFlexAlign.lt-xl", ""], ["", "fxFlexAlign.gt-xs", ""], ["", "fxFlexAlign.gt-sm", ""], ["", "fxFlexAlign.gt-md", ""], ["", "fxFlexAlign.gt-lg", ""]],
        inputs: {
          fxFlexAlign: "fxFlexAlign",
          "fxFlexAlign.xs": "fxFlexAlign.xs",
          "fxFlexAlign.sm": "fxFlexAlign.sm",
          "fxFlexAlign.md": "fxFlexAlign.md",
          "fxFlexAlign.lg": "fxFlexAlign.lg",
          "fxFlexAlign.xl": "fxFlexAlign.xl",
          "fxFlexAlign.lt-sm": "fxFlexAlign.lt-sm",
          "fxFlexAlign.lt-md": "fxFlexAlign.lt-md",
          "fxFlexAlign.lt-lg": "fxFlexAlign.lt-lg",
          "fxFlexAlign.lt-xl": "fxFlexAlign.lt-xl",
          "fxFlexAlign.gt-xs": "fxFlexAlign.gt-xs",
          "fxFlexAlign.gt-sm": "fxFlexAlign.gt-sm",
          "fxFlexAlign.gt-md": "fxFlexAlign.gt-md",
          "fxFlexAlign.gt-lg": "fxFlexAlign.gt-lg"
        },
        features: [i0.ɵɵInheritDefinitionFeature]
      });
    }
  }
  return DefaultFlexAlignDirective;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();

/**
 * @license
 * Copyright Google LLC All Rights Reserved.
 *
 * Use of this source code is governed by an MIT-style license that can be
 * found in the LICENSE file at https://angular.io/license
 */
const FLEX_FILL_CSS = {
  margin: 0,
  width: '100%',
  height: '100%',
  'min-width': '100%',
  'min-height': '100%'
};
let FlexFillStyleBuilder = /*#__PURE__*/(() => {
  class FlexFillStyleBuilder extends StyleBuilder {
    buildStyles(_input) {
      return FLEX_FILL_CSS;
    }
    static {
      this.ɵfac = /* @__PURE__ */(() => {
        let ɵFlexFillStyleBuilder_BaseFactory;
        return function FlexFillStyleBuilder_Factory(__ngFactoryType__) {
          return (ɵFlexFillStyleBuilder_BaseFactory || (ɵFlexFillStyleBuilder_BaseFactory = i0.ɵɵgetInheritedFactory(FlexFillStyleBuilder)))(__ngFactoryType__ || FlexFillStyleBuilder);
        };
      })();
    }
    static {
      this.ɵprov = /* @__PURE__ */i0.ɵɵdefineInjectable({
        token: FlexFillStyleBuilder,
        factory: FlexFillStyleBuilder.ɵfac,
        providedIn: 'root'
      });
    }
  }
  return FlexFillStyleBuilder;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();
/**
 * 'fxFill' flexbox styling directive
 *  Maximizes width and height of element in a layout container
 *
 *  NOTE: fxFill is NOT responsive API!!
 */
let FlexFillDirective = /*#__PURE__*/(() => {
  class FlexFillDirective extends BaseDirective2 {
    constructor(elRef, styleUtils, styleBuilder, marshal) {
      super(elRef, styleBuilder, styleUtils, marshal);
      this.styleCache = flexFillCache;
      this.addStyles('');
    }
    static {
      this.ɵfac = function FlexFillDirective_Factory(__ngFactoryType__) {
        return new (__ngFactoryType__ || FlexFillDirective)(i0.ɵɵdirectiveInject(i0.ElementRef), i0.ɵɵdirectiveInject(i1.StyleUtils), i0.ɵɵdirectiveInject(FlexFillStyleBuilder), i0.ɵɵdirectiveInject(i1.MediaMarshaller));
      };
    }
    static {
      this.ɵdir = /* @__PURE__ */i0.ɵɵdefineDirective({
        type: FlexFillDirective,
        selectors: [["", "fxFill", ""], ["", "fxFlexFill", ""]],
        features: [i0.ɵɵInheritDefinitionFeature]
      });
    }
  }
  return FlexFillDirective;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();
const flexFillCache = new Map();
let FlexOffsetStyleBuilder = /*#__PURE__*/(() => {
  class FlexOffsetStyleBuilder extends StyleBuilder {
    constructor(_config) {
      super();
      this._config = _config;
    }
    buildStyles(offset, parent) {
      offset ||= '0';
      offset = _multiply(offset, this._config.multiplier);
      const isPercent = String(offset).indexOf('%') > -1;
      const isPx = String(offset).indexOf('px') > -1;
      if (!isPx && !isPercent && !isNaN(+offset)) {
        offset = `${offset}%`;
      }
      const horizontalLayoutKey = parent.isRtl ? 'margin-right' : 'margin-left';
      const styles = isFlowHorizontal(parent.layout) ? {
        [horizontalLayoutKey]: offset
      } : {
        'margin-top': offset
      };
      return styles;
    }
    static {
      this.ɵfac = function FlexOffsetStyleBuilder_Factory(__ngFactoryType__) {
        return new (__ngFactoryType__ || FlexOffsetStyleBuilder)(i0.ɵɵinject(LAYOUT_CONFIG));
      };
    }
    static {
      this.ɵprov = /* @__PURE__ */i0.ɵɵdefineInjectable({
        token: FlexOffsetStyleBuilder,
        factory: FlexOffsetStyleBuilder.ɵfac,
        providedIn: 'root'
      });
    }
  }
  return FlexOffsetStyleBuilder;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();
const inputs$5 = ['fxFlexOffset', 'fxFlexOffset.xs', 'fxFlexOffset.sm', 'fxFlexOffset.md', 'fxFlexOffset.lg', 'fxFlexOffset.xl', 'fxFlexOffset.lt-sm', 'fxFlexOffset.lt-md', 'fxFlexOffset.lt-lg', 'fxFlexOffset.lt-xl', 'fxFlexOffset.gt-xs', 'fxFlexOffset.gt-sm', 'fxFlexOffset.gt-md', 'fxFlexOffset.gt-lg'];
const selector$5 = `
  [fxFlexOffset], [fxFlexOffset.xs], [fxFlexOffset.sm], [fxFlexOffset.md],
  [fxFlexOffset.lg], [fxFlexOffset.xl], [fxFlexOffset.lt-sm], [fxFlexOffset.lt-md],
  [fxFlexOffset.lt-lg], [fxFlexOffset.lt-xl], [fxFlexOffset.gt-xs], [fxFlexOffset.gt-sm],
  [fxFlexOffset.gt-md], [fxFlexOffset.gt-lg]
`;
/**
 * 'flex-offset' flexbox styling directive
 * Configures the 'margin-left' of the element in a layout container
 */
let FlexOffsetDirective = /*#__PURE__*/(() => {
  class FlexOffsetDirective extends BaseDirective2 {
    constructor(elRef, directionality, styleBuilder, marshal, styler) {
      super(elRef, styleBuilder, styler, marshal);
      this.directionality = directionality;
      this.DIRECTIVE_KEY = 'flex-offset';
      this.init([this.directionality.change]);
      // Parent DOM `layout-gap` with affect the nested child with `flex-offset`
      if (this.parentElement) {
        this.marshal.trackValue(this.parentElement, 'layout-gap').pipe(takeUntil(this.destroySubject)).subscribe(this.triggerUpdate.bind(this));
      }
    }
    // *********************************************
    // Protected methods
    // *********************************************
    /**
     * Using the current fxFlexOffset value, update the inline CSS
     * NOTE: this will assign `margin-left` if the parent flex-direction == 'row',
     *       otherwise `margin-top` is used for the offset.
     */
    updateWithValue(value = '') {
      // The flex-direction of this element's flex container. Defaults to 'row'.
      const layout = this.getFlexFlowDirection(this.parentElement, true);
      const isRtl = this.directionality.value === 'rtl';
      if (layout === 'row' && isRtl) {
        this.styleCache = flexOffsetCacheRowRtl;
      } else if (layout === 'row' && !isRtl) {
        this.styleCache = flexOffsetCacheRowLtr;
      } else if (layout === 'column' && isRtl) {
        this.styleCache = flexOffsetCacheColumnRtl;
      } else if (layout === 'column' && !isRtl) {
        this.styleCache = flexOffsetCacheColumnLtr;
      }
      this.addStyles(value + '', {
        layout,
        isRtl
      });
    }
    static {
      this.ɵfac = function FlexOffsetDirective_Factory(__ngFactoryType__) {
        return new (__ngFactoryType__ || FlexOffsetDirective)(i0.ɵɵdirectiveInject(i0.ElementRef), i0.ɵɵdirectiveInject(i1$1.Directionality), i0.ɵɵdirectiveInject(FlexOffsetStyleBuilder), i0.ɵɵdirectiveInject(i1.MediaMarshaller), i0.ɵɵdirectiveInject(i1.StyleUtils));
      };
    }
    static {
      this.ɵdir = /* @__PURE__ */i0.ɵɵdefineDirective({
        type: FlexOffsetDirective,
        features: [i0.ɵɵInheritDefinitionFeature]
      });
    }
  }
  return FlexOffsetDirective;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();
let DefaultFlexOffsetDirective = /*#__PURE__*/(() => {
  class DefaultFlexOffsetDirective extends FlexOffsetDirective {
    constructor() {
      super(...arguments);
      this.inputs = inputs$5;
    }
    static {
      this.ɵfac = /* @__PURE__ */(() => {
        let ɵDefaultFlexOffsetDirective_BaseFactory;
        return function DefaultFlexOffsetDirective_Factory(__ngFactoryType__) {
          return (ɵDefaultFlexOffsetDirective_BaseFactory || (ɵDefaultFlexOffsetDirective_BaseFactory = i0.ɵɵgetInheritedFactory(DefaultFlexOffsetDirective)))(__ngFactoryType__ || DefaultFlexOffsetDirective);
        };
      })();
    }
    static {
      this.ɵdir = /* @__PURE__ */i0.ɵɵdefineDirective({
        type: DefaultFlexOffsetDirective,
        selectors: [["", "fxFlexOffset", ""], ["", "fxFlexOffset.xs", ""], ["", "fxFlexOffset.sm", ""], ["", "fxFlexOffset.md", ""], ["", "fxFlexOffset.lg", ""], ["", "fxFlexOffset.xl", ""], ["", "fxFlexOffset.lt-sm", ""], ["", "fxFlexOffset.lt-md", ""], ["", "fxFlexOffset.lt-lg", ""], ["", "fxFlexOffset.lt-xl", ""], ["", "fxFlexOffset.gt-xs", ""], ["", "fxFlexOffset.gt-sm", ""], ["", "fxFlexOffset.gt-md", ""], ["", "fxFlexOffset.gt-lg", ""]],
        inputs: {
          fxFlexOffset: "fxFlexOffset",
          "fxFlexOffset.xs": "fxFlexOffset.xs",
          "fxFlexOffset.sm": "fxFlexOffset.sm",
          "fxFlexOffset.md": "fxFlexOffset.md",
          "fxFlexOffset.lg": "fxFlexOffset.lg",
          "fxFlexOffset.xl": "fxFlexOffset.xl",
          "fxFlexOffset.lt-sm": "fxFlexOffset.lt-sm",
          "fxFlexOffset.lt-md": "fxFlexOffset.lt-md",
          "fxFlexOffset.lt-lg": "fxFlexOffset.lt-lg",
          "fxFlexOffset.lt-xl": "fxFlexOffset.lt-xl",
          "fxFlexOffset.gt-xs": "fxFlexOffset.gt-xs",
          "fxFlexOffset.gt-sm": "fxFlexOffset.gt-sm",
          "fxFlexOffset.gt-md": "fxFlexOffset.gt-md",
          "fxFlexOffset.gt-lg": "fxFlexOffset.gt-lg"
        },
        features: [i0.ɵɵInheritDefinitionFeature]
      });
    }
  }
  return DefaultFlexOffsetDirective;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();
const flexOffsetCacheRowRtl = new Map();
const flexOffsetCacheColumnRtl = new Map();
const flexOffsetCacheRowLtr = new Map();
const flexOffsetCacheColumnLtr = new Map();

/**
 * @license
 * Copyright Google LLC All Rights Reserved.
 *
 * Use of this source code is governed by an MIT-style license that can be
 * found in the LICENSE file at https://angular.io/license
 */
let FlexOrderStyleBuilder = /*#__PURE__*/(() => {
  class FlexOrderStyleBuilder extends StyleBuilder {
    buildStyles(value) {
      return {
        order: value && parseInt(value, 10) || ''
      };
    }
    static {
      this.ɵfac = /* @__PURE__ */(() => {
        let ɵFlexOrderStyleBuilder_BaseFactory;
        return function FlexOrderStyleBuilder_Factory(__ngFactoryType__) {
          return (ɵFlexOrderStyleBuilder_BaseFactory || (ɵFlexOrderStyleBuilder_BaseFactory = i0.ɵɵgetInheritedFactory(FlexOrderStyleBuilder)))(__ngFactoryType__ || FlexOrderStyleBuilder);
        };
      })();
    }
    static {
      this.ɵprov = /* @__PURE__ */i0.ɵɵdefineInjectable({
        token: FlexOrderStyleBuilder,
        factory: FlexOrderStyleBuilder.ɵfac,
        providedIn: 'root'
      });
    }
  }
  return FlexOrderStyleBuilder;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();
const inputs$4 = ['fxFlexOrder', 'fxFlexOrder.xs', 'fxFlexOrder.sm', 'fxFlexOrder.md', 'fxFlexOrder.lg', 'fxFlexOrder.xl', 'fxFlexOrder.lt-sm', 'fxFlexOrder.lt-md', 'fxFlexOrder.lt-lg', 'fxFlexOrder.lt-xl', 'fxFlexOrder.gt-xs', 'fxFlexOrder.gt-sm', 'fxFlexOrder.gt-md', 'fxFlexOrder.gt-lg'];
const selector$4 = `
  [fxFlexOrder], [fxFlexOrder.xs], [fxFlexOrder.sm], [fxFlexOrder.md],
  [fxFlexOrder.lg], [fxFlexOrder.xl], [fxFlexOrder.lt-sm], [fxFlexOrder.lt-md],
  [fxFlexOrder.lt-lg], [fxFlexOrder.lt-xl], [fxFlexOrder.gt-xs], [fxFlexOrder.gt-sm],
  [fxFlexOrder.gt-md], [fxFlexOrder.gt-lg]
`;
/**
 * 'flex-order' flexbox styling directive
 * Configures the positional ordering of the element in a sorted layout container
 * @see https://css-tricks.com/almanac/properties/o/order/
 */
let FlexOrderDirective = /*#__PURE__*/(() => {
  class FlexOrderDirective extends BaseDirective2 {
    constructor(elRef, styleUtils, styleBuilder, marshal) {
      super(elRef, styleBuilder, styleUtils, marshal);
      this.DIRECTIVE_KEY = 'flex-order';
      this.styleCache = flexOrderCache;
      this.init();
    }
    updateWithValue(input) {
      super.updateWithValue(input);
      if (this.parentElement) {
        this.marshal.triggerUpdate(this.parentElement, 'layout-gap');
      }
    }
    static {
      this.ɵfac = function FlexOrderDirective_Factory(__ngFactoryType__) {
        return new (__ngFactoryType__ || FlexOrderDirective)(i0.ɵɵdirectiveInject(i0.ElementRef), i0.ɵɵdirectiveInject(i1.StyleUtils), i0.ɵɵdirectiveInject(FlexOrderStyleBuilder), i0.ɵɵdirectiveInject(i1.MediaMarshaller));
      };
    }
    static {
      this.ɵdir = /* @__PURE__ */i0.ɵɵdefineDirective({
        type: FlexOrderDirective,
        features: [i0.ɵɵInheritDefinitionFeature]
      });
    }
  }
  return FlexOrderDirective;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();
const flexOrderCache = new Map();
let DefaultFlexOrderDirective = /*#__PURE__*/(() => {
  class DefaultFlexOrderDirective extends FlexOrderDirective {
    constructor() {
      super(...arguments);
      this.inputs = inputs$4;
    }
    static {
      this.ɵfac = /* @__PURE__ */(() => {
        let ɵDefaultFlexOrderDirective_BaseFactory;
        return function DefaultFlexOrderDirective_Factory(__ngFactoryType__) {
          return (ɵDefaultFlexOrderDirective_BaseFactory || (ɵDefaultFlexOrderDirective_BaseFactory = i0.ɵɵgetInheritedFactory(DefaultFlexOrderDirective)))(__ngFactoryType__ || DefaultFlexOrderDirective);
        };
      })();
    }
    static {
      this.ɵdir = /* @__PURE__ */i0.ɵɵdefineDirective({
        type: DefaultFlexOrderDirective,
        selectors: [["", "fxFlexOrder", ""], ["", "fxFlexOrder.xs", ""], ["", "fxFlexOrder.sm", ""], ["", "fxFlexOrder.md", ""], ["", "fxFlexOrder.lg", ""], ["", "fxFlexOrder.xl", ""], ["", "fxFlexOrder.lt-sm", ""], ["", "fxFlexOrder.lt-md", ""], ["", "fxFlexOrder.lt-lg", ""], ["", "fxFlexOrder.lt-xl", ""], ["", "fxFlexOrder.gt-xs", ""], ["", "fxFlexOrder.gt-sm", ""], ["", "fxFlexOrder.gt-md", ""], ["", "fxFlexOrder.gt-lg", ""]],
        inputs: {
          fxFlexOrder: "fxFlexOrder",
          "fxFlexOrder.xs": "fxFlexOrder.xs",
          "fxFlexOrder.sm": "fxFlexOrder.sm",
          "fxFlexOrder.md": "fxFlexOrder.md",
          "fxFlexOrder.lg": "fxFlexOrder.lg",
          "fxFlexOrder.xl": "fxFlexOrder.xl",
          "fxFlexOrder.lt-sm": "fxFlexOrder.lt-sm",
          "fxFlexOrder.lt-md": "fxFlexOrder.lt-md",
          "fxFlexOrder.lt-lg": "fxFlexOrder.lt-lg",
          "fxFlexOrder.lt-xl": "fxFlexOrder.lt-xl",
          "fxFlexOrder.gt-xs": "fxFlexOrder.gt-xs",
          "fxFlexOrder.gt-sm": "fxFlexOrder.gt-sm",
          "fxFlexOrder.gt-md": "fxFlexOrder.gt-md",
          "fxFlexOrder.gt-lg": "fxFlexOrder.gt-lg"
        },
        features: [i0.ɵɵInheritDefinitionFeature]
      });
    }
  }
  return DefaultFlexOrderDirective;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();

/**
 * @license
 * Copyright Google LLC All Rights Reserved.
 *
 * Use of this source code is governed by an MIT-style license that can be
 * found in the LICENSE file at https://angular.io/license
 */
let FlexStyleBuilder = /*#__PURE__*/(() => {
  class FlexStyleBuilder extends StyleBuilder {
    constructor(layoutConfig) {
      super();
      this.layoutConfig = layoutConfig;
    }
    buildStyles(input, parent) {
      let [grow, shrink, ...basisParts] = input.split(' ');
      let basis = basisParts.join(' ');
      // The flex-direction of this element's flex container. Defaults to 'row'.
      const direction = parent.direction.indexOf('column') > -1 ? 'column' : 'row';
      const max = isFlowHorizontal(direction) ? 'max-width' : 'max-height';
      const min = isFlowHorizontal(direction) ? 'min-width' : 'min-height';
      const hasCalc = String(basis).indexOf('calc') > -1;
      const usingCalc = hasCalc || basis === 'auto';
      const isPercent = String(basis).indexOf('%') > -1 && !hasCalc;
      const hasUnits = String(basis).indexOf('px') > -1 || String(basis).indexOf('rem') > -1 || String(basis).indexOf('em') > -1 || String(basis).indexOf('vw') > -1 || String(basis).indexOf('vh') > -1;
      let isValue = hasCalc || hasUnits;
      grow = grow == '0' ? 0 : grow;
      shrink = shrink == '0' ? 0 : shrink;
      // make box inflexible when shrink and grow are both zero
      // should not set a min when the grow is zero
      // should not set a max when the shrink is zero
      const isFixed = !grow && !shrink;
      let css = {};
      // flex-basis allows you to specify the initial/starting main-axis size of the element,
      // before anything else is computed. It can either be a percentage or an absolute value.
      // It is, however, not the breaking point for flex-grow/shrink properties
      //
      // flex-grow can be seen as this:
      //   0: Do not stretch. Either size to element's content width, or obey 'flex-basis'.
      //   1: (Default value). Stretch; will be the same size to all other flex items on
      //       the same row since they have a default value of 1.
      //   ≥2 (integer n): Stretch. Will be n times the size of other elements
      //      with 'flex-grow: 1' on the same row.
      // Use `null` to clear existing styles.
      const clearStyles = {
        'max-width': null,
        'max-height': null,
        'min-width': null,
        'min-height': null
      };
      switch (basis || '') {
        case '':
          const useColumnBasisZero = this.layoutConfig.useColumnBasisZero !== false;
          basis = direction === 'row' ? '0%' : useColumnBasisZero ? '0.000000001px' : 'auto';
          break;
        case 'initial': // default
        case 'nogrow':
          grow = 0;
          basis = 'auto';
          break;
        case 'grow':
          basis = '100%';
          break;
        case 'noshrink':
          shrink = 0;
          basis = 'auto';
          break;
        case 'auto':
          break;
        case 'none':
          grow = 0;
          shrink = 0;
          basis = 'auto';
          break;
        default:
          // Defaults to percentage sizing unless `px` is explicitly set
          if (!isValue && !isPercent && !isNaN(basis)) {
            basis = basis + '%';
          }
          // Fix for issue 280
          if (basis === '0%') {
            isValue = true;
          }
          if (basis === '0px') {
            basis = '0%';
          }
          // fix issue #5345
          if (hasCalc) {
            css = extendObject(clearStyles, {
              'flex-grow': grow,
              'flex-shrink': shrink,
              'flex-basis': isValue ? basis : '100%'
            });
          } else {
            css = extendObject(clearStyles, {
              flex: `${grow} ${shrink} ${isValue ? basis : '100%'}`
            });
          }
          break;
      }
      if (!(css['flex'] || css['flex-grow'])) {
        if (hasCalc) {
          css = extendObject(clearStyles, {
            'flex-grow': grow,
            'flex-shrink': shrink,
            'flex-basis': basis
          });
        } else {
          css = extendObject(clearStyles, {
            flex: `${grow} ${shrink} ${basis}`
          });
        }
      }
      // Fix for issues 277, 534, and 728
      if (basis !== '0%' && basis !== '0px' && basis !== '0.000000001px' && basis !== 'auto') {
        css[min] = isFixed || isValue && grow ? basis : null;
        css[max] = isFixed || !usingCalc && shrink ? basis : null;
      }
      // Fix for issue 528
      if (!css[min] && !css[max]) {
        if (hasCalc) {
          css = extendObject(clearStyles, {
            'flex-grow': grow,
            'flex-shrink': shrink,
            'flex-basis': basis
          });
        } else {
          css = extendObject(clearStyles, {
            flex: `${grow} ${shrink} ${basis}`
          });
        }
      } else {
        // Fix for issue 660
        if (parent.hasWrap) {
          css[hasCalc ? 'flex-basis' : 'flex'] = css[max] ? hasCalc ? css[max] : `${grow} ${shrink} ${css[max]}` : hasCalc ? css[min] : `${grow} ${shrink} ${css[min]}`;
        }
      }
      return extendObject(css, {
        'box-sizing': 'border-box'
      });
    }
    static {
      this.ɵfac = function FlexStyleBuilder_Factory(__ngFactoryType__) {
        return new (__ngFactoryType__ || FlexStyleBuilder)(i0.ɵɵinject(LAYOUT_CONFIG));
      };
    }
    static {
      this.ɵprov = /* @__PURE__ */i0.ɵɵdefineInjectable({
        token: FlexStyleBuilder,
        factory: FlexStyleBuilder.ɵfac,
        providedIn: 'root'
      });
    }
  }
  return FlexStyleBuilder;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();
const inputs$3 = ['fxFlex', 'fxFlex.xs', 'fxFlex.sm', 'fxFlex.md', 'fxFlex.lg', 'fxFlex.xl', 'fxFlex.lt-sm', 'fxFlex.lt-md', 'fxFlex.lt-lg', 'fxFlex.lt-xl', 'fxFlex.gt-xs', 'fxFlex.gt-sm', 'fxFlex.gt-md', 'fxFlex.gt-lg'];
const selector$3 = `
  [fxFlex], [fxFlex.xs], [fxFlex.sm], [fxFlex.md],
  [fxFlex.lg], [fxFlex.xl], [fxFlex.lt-sm], [fxFlex.lt-md],
  [fxFlex.lt-lg], [fxFlex.lt-xl], [fxFlex.gt-xs], [fxFlex.gt-sm],
  [fxFlex.gt-md], [fxFlex.gt-lg]
`;
/**
 * Directive to control the size of a flex item using flex-basis, flex-grow, and flex-shrink.
 * Corresponds to the css `flex` shorthand property.
 *
 * @see https://css-tricks.com/snippets/css/a-guide-to-flexbox/
 */
let FlexDirective = /*#__PURE__*/(() => {
  class FlexDirective extends BaseDirective2 {
    get shrink() {
      return this.flexShrink;
    }
    set shrink(value) {
      this.flexShrink = value || '1';
      this.triggerReflow();
    }
    get grow() {
      return this.flexGrow;
    }
    set grow(value) {
      this.flexGrow = value || '1';
      this.triggerReflow();
    }
    constructor(elRef, styleUtils, layoutConfig, styleBuilder, marshal) {
      super(elRef, styleBuilder, styleUtils, marshal);
      this.layoutConfig = layoutConfig;
      this.marshal = marshal;
      this.DIRECTIVE_KEY = 'flex';
      this.direction = undefined;
      this.wrap = undefined;
      this.flexGrow = '1';
      this.flexShrink = '1';
      this.init();
    }
    ngOnInit() {
      if (this.parentElement) {
        this.marshal.trackValue(this.parentElement, 'layout').pipe(takeUntil(this.destroySubject)).subscribe(this.onLayoutChange.bind(this));
        this.marshal.trackValue(this.nativeElement, 'layout-align').pipe(takeUntil(this.destroySubject)).subscribe(this.triggerReflow.bind(this));
      }
    }
    /**
     * Caches the parent container's 'flex-direction' and updates the element's style.
     * Used as a handler for layout change events from the parent flex container.
     */
    onLayoutChange(matcher) {
      const layout = matcher.value;
      const layoutParts = layout.split(' ');
      this.direction = layoutParts[0];
      this.wrap = layoutParts[1] !== undefined && layoutParts[1] === 'wrap';
      this.triggerUpdate();
    }
    /** Input to this is exclusively the basis input value */
    updateWithValue(value) {
      const addFlexToParent = this.layoutConfig.addFlexToParent !== false;
      if (this.direction === undefined) {
        this.direction = this.getFlexFlowDirection(this.parentElement, addFlexToParent);
      }
      if (this.wrap === undefined) {
        this.wrap = this.hasWrap(this.parentElement);
      }
      const direction = this.direction;
      const isHorizontal = direction.startsWith('row');
      const hasWrap = this.wrap;
      if (isHorizontal && hasWrap) {
        this.styleCache = flexRowWrapCache;
      } else if (isHorizontal && !hasWrap) {
        this.styleCache = flexRowCache;
      } else if (!isHorizontal && hasWrap) {
        this.styleCache = flexColumnWrapCache;
      } else if (!isHorizontal && !hasWrap) {
        this.styleCache = flexColumnCache;
      }
      const basis = String(value).replace(';', '');
      const parts = validateBasis(basis, this.flexGrow, this.flexShrink);
      this.addStyles(parts.join(' '), {
        direction,
        hasWrap
      });
    }
    /** Trigger a style reflow, usually based on a shrink/grow input event */
    triggerReflow() {
      const activatedValue = this.activatedValue;
      if (activatedValue !== undefined) {
        const parts = validateBasis(activatedValue + '', this.flexGrow, this.flexShrink);
        this.marshal.updateElement(this.nativeElement, this.DIRECTIVE_KEY, parts.join(' '));
      }
    }
    static {
      this.ɵfac = function FlexDirective_Factory(__ngFactoryType__) {
        return new (__ngFactoryType__ || FlexDirective)(i0.ɵɵdirectiveInject(i0.ElementRef), i0.ɵɵdirectiveInject(i1.StyleUtils), i0.ɵɵdirectiveInject(LAYOUT_CONFIG), i0.ɵɵdirectiveInject(FlexStyleBuilder), i0.ɵɵdirectiveInject(i1.MediaMarshaller));
      };
    }
    static {
      this.ɵdir = /* @__PURE__ */i0.ɵɵdefineDirective({
        type: FlexDirective,
        inputs: {
          shrink: [0, "fxShrink", "shrink"],
          grow: [0, "fxGrow", "grow"]
        },
        features: [i0.ɵɵInheritDefinitionFeature]
      });
    }
  }
  return FlexDirective;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();
let DefaultFlexDirective = /*#__PURE__*/(() => {
  class DefaultFlexDirective extends FlexDirective {
    constructor() {
      super(...arguments);
      this.inputs = inputs$3;
    }
    static {
      this.ɵfac = /* @__PURE__ */(() => {
        let ɵDefaultFlexDirective_BaseFactory;
        return function DefaultFlexDirective_Factory(__ngFactoryType__) {
          return (ɵDefaultFlexDirective_BaseFactory || (ɵDefaultFlexDirective_BaseFactory = i0.ɵɵgetInheritedFactory(DefaultFlexDirective)))(__ngFactoryType__ || DefaultFlexDirective);
        };
      })();
    }
    static {
      this.ɵdir = /* @__PURE__ */i0.ɵɵdefineDirective({
        type: DefaultFlexDirective,
        selectors: [["", "fxFlex", ""], ["", "fxFlex.xs", ""], ["", "fxFlex.sm", ""], ["", "fxFlex.md", ""], ["", "fxFlex.lg", ""], ["", "fxFlex.xl", ""], ["", "fxFlex.lt-sm", ""], ["", "fxFlex.lt-md", ""], ["", "fxFlex.lt-lg", ""], ["", "fxFlex.lt-xl", ""], ["", "fxFlex.gt-xs", ""], ["", "fxFlex.gt-sm", ""], ["", "fxFlex.gt-md", ""], ["", "fxFlex.gt-lg", ""]],
        inputs: {
          fxFlex: "fxFlex",
          "fxFlex.xs": "fxFlex.xs",
          "fxFlex.sm": "fxFlex.sm",
          "fxFlex.md": "fxFlex.md",
          "fxFlex.lg": "fxFlex.lg",
          "fxFlex.xl": "fxFlex.xl",
          "fxFlex.lt-sm": "fxFlex.lt-sm",
          "fxFlex.lt-md": "fxFlex.lt-md",
          "fxFlex.lt-lg": "fxFlex.lt-lg",
          "fxFlex.lt-xl": "fxFlex.lt-xl",
          "fxFlex.gt-xs": "fxFlex.gt-xs",
          "fxFlex.gt-sm": "fxFlex.gt-sm",
          "fxFlex.gt-md": "fxFlex.gt-md",
          "fxFlex.gt-lg": "fxFlex.gt-lg"
        },
        features: [i0.ɵɵInheritDefinitionFeature]
      });
    }
  }
  return DefaultFlexDirective;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();
const flexRowCache = new Map();
const flexColumnCache = new Map();
const flexRowWrapCache = new Map();
const flexColumnWrapCache = new Map();

/**
 * @license
 * Copyright Google LLC All Rights Reserved.
 *
 * Use of this source code is governed by an MIT-style license that can be
 * found in the LICENSE file at https://angular.io/license
 */
let LayoutAlignStyleBuilder = /*#__PURE__*/(() => {
  class LayoutAlignStyleBuilder extends StyleBuilder {
    buildStyles(align, parent) {
      const css = {},
        [mainAxis, crossAxis] = align.split(' ');
      // Main axis
      switch (mainAxis) {
        case 'center':
          css['justify-content'] = 'center';
          break;
        case 'space-around':
          css['justify-content'] = 'space-around';
          break;
        case 'space-between':
          css['justify-content'] = 'space-between';
          break;
        case 'space-evenly':
          css['justify-content'] = 'space-evenly';
          break;
        case 'end':
        case 'flex-end':
          css['justify-content'] = 'flex-end';
          break;
        case 'start':
        case 'flex-start':
        default:
          // default main axis
          css['justify-content'] = 'flex-start';
          break;
      }
      // Cross-axis
      switch (crossAxis) {
        case 'start':
        case 'flex-start':
          css['align-items'] = css['align-content'] = 'flex-start';
          break;
        case 'center':
          css['align-items'] = css['align-content'] = 'center';
          break;
        case 'end':
        case 'flex-end':
          css['align-items'] = css['align-content'] = 'flex-end';
          break;
        case 'space-between':
          css['align-content'] = 'space-between';
          css['align-items'] = 'stretch';
          break;
        case 'space-around':
          css['align-content'] = 'space-around';
          css['align-items'] = 'stretch';
          break;
        case 'baseline':
          css['align-content'] = 'stretch';
          css['align-items'] = 'baseline';
          break;
        case 'stretch':
        default:
          // 'stretch'
          // default cross axis
          css['align-items'] = css['align-content'] = 'stretch';
          break;
      }
      return extendObject(css, {
        display: parent.inline ? 'inline-flex' : 'flex',
        'flex-direction': parent.layout,
        'box-sizing': 'border-box',
        'max-width': crossAxis === 'stretch' ? !isFlowHorizontal(parent.layout) ? '100%' : null : null,
        'max-height': crossAxis === 'stretch' ? isFlowHorizontal(parent.layout) ? '100%' : null : null
      });
    }
    static {
      this.ɵfac = /* @__PURE__ */(() => {
        let ɵLayoutAlignStyleBuilder_BaseFactory;
        return function LayoutAlignStyleBuilder_Factory(__ngFactoryType__) {
          return (ɵLayoutAlignStyleBuilder_BaseFactory || (ɵLayoutAlignStyleBuilder_BaseFactory = i0.ɵɵgetInheritedFactory(LayoutAlignStyleBuilder)))(__ngFactoryType__ || LayoutAlignStyleBuilder);
        };
      })();
    }
    static {
      this.ɵprov = /* @__PURE__ */i0.ɵɵdefineInjectable({
        token: LayoutAlignStyleBuilder,
        factory: LayoutAlignStyleBuilder.ɵfac,
        providedIn: 'root'
      });
    }
  }
  return LayoutAlignStyleBuilder;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();
const inputs$2 = ['fxLayoutAlign', 'fxLayoutAlign.xs', 'fxLayoutAlign.sm', 'fxLayoutAlign.md', 'fxLayoutAlign.lg', 'fxLayoutAlign.xl', 'fxLayoutAlign.lt-sm', 'fxLayoutAlign.lt-md', 'fxLayoutAlign.lt-lg', 'fxLayoutAlign.lt-xl', 'fxLayoutAlign.gt-xs', 'fxLayoutAlign.gt-sm', 'fxLayoutAlign.gt-md', 'fxLayoutAlign.gt-lg'];
const selector$2 = `
  [fxLayoutAlign], [fxLayoutAlign.xs], [fxLayoutAlign.sm], [fxLayoutAlign.md],
  [fxLayoutAlign.lg], [fxLayoutAlign.xl], [fxLayoutAlign.lt-sm], [fxLayoutAlign.lt-md],
  [fxLayoutAlign.lt-lg], [fxLayoutAlign.lt-xl], [fxLayoutAlign.gt-xs], [fxLayoutAlign.gt-sm],
  [fxLayoutAlign.gt-md], [fxLayoutAlign.gt-lg]
`;
/**
 * 'layout-align' flexbox styling directive
 *  Defines positioning of child elements along main and cross axis in a layout container
 *  Optional values: {main-axis} values or {main-axis cross-axis} value pairs
 *
 *  @see https://css-tricks.com/almanac/properties/j/justify-content/
 *  @see https://css-tricks.com/almanac/properties/a/align-items/
 *  @see https://css-tricks.com/almanac/properties/a/align-content/
 */
let LayoutAlignDirective = /*#__PURE__*/(() => {
  class LayoutAlignDirective extends BaseDirective2 {
    constructor(elRef, styleUtils, styleBuilder, marshal) {
      super(elRef, styleBuilder, styleUtils, marshal);
      this.DIRECTIVE_KEY = 'layout-align';
      this.layout = 'row'; // default flex-direction
      this.inline = false; // default inline value
      this.init();
      this.marshal.trackValue(this.nativeElement, 'layout').pipe(takeUntil(this.destroySubject)).subscribe(this.onLayoutChange.bind(this));
    }
    // *********************************************
    // Protected methods
    // *********************************************
    /**
     *
     */
    updateWithValue(value) {
      const layout = this.layout || 'row';
      const inline = this.inline;
      if (layout === 'row' && inline) {
        this.styleCache = layoutAlignHorizontalInlineCache;
      } else if (layout === 'row' && !inline) {
        this.styleCache = layoutAlignHorizontalCache;
      } else if (layout === 'row-reverse' && inline) {
        this.styleCache = layoutAlignHorizontalRevInlineCache;
      } else if (layout === 'row-reverse' && !inline) {
        this.styleCache = layoutAlignHorizontalRevCache;
      } else if (layout === 'column' && inline) {
        this.styleCache = layoutAlignVerticalInlineCache;
      } else if (layout === 'column' && !inline) {
        this.styleCache = layoutAlignVerticalCache;
      } else if (layout === 'column-reverse' && inline) {
        this.styleCache = layoutAlignVerticalRevInlineCache;
      } else if (layout === 'column-reverse' && !inline) {
        this.styleCache = layoutAlignVerticalRevCache;
      }
      this.addStyles(value, {
        layout,
        inline
      });
    }
    /**
     * Cache the parent container 'flex-direction' and update the 'flex' styles
     */
    onLayoutChange(matcher) {
      const layoutKeys = matcher.value.split(' ');
      this.layout = layoutKeys[0];
      this.inline = matcher.value.includes('inline');
      if (!LAYOUT_VALUES.find(x => x === this.layout)) {
        this.layout = 'row';
      }
      this.triggerUpdate();
    }
    static {
      this.ɵfac = function LayoutAlignDirective_Factory(__ngFactoryType__) {
        return new (__ngFactoryType__ || LayoutAlignDirective)(i0.ɵɵdirectiveInject(i0.ElementRef), i0.ɵɵdirectiveInject(i1.StyleUtils), i0.ɵɵdirectiveInject(LayoutAlignStyleBuilder), i0.ɵɵdirectiveInject(i1.MediaMarshaller));
      };
    }
    static {
      this.ɵdir = /* @__PURE__ */i0.ɵɵdefineDirective({
        type: LayoutAlignDirective,
        features: [i0.ɵɵInheritDefinitionFeature]
      });
    }
  }
  return LayoutAlignDirective;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();
let DefaultLayoutAlignDirective = /*#__PURE__*/(() => {
  class DefaultLayoutAlignDirective extends LayoutAlignDirective {
    constructor() {
      super(...arguments);
      this.inputs = inputs$2;
    }
    static {
      this.ɵfac = /* @__PURE__ */(() => {
        let ɵDefaultLayoutAlignDirective_BaseFactory;
        return function DefaultLayoutAlignDirective_Factory(__ngFactoryType__) {
          return (ɵDefaultLayoutAlignDirective_BaseFactory || (ɵDefaultLayoutAlignDirective_BaseFactory = i0.ɵɵgetInheritedFactory(DefaultLayoutAlignDirective)))(__ngFactoryType__ || DefaultLayoutAlignDirective);
        };
      })();
    }
    static {
      this.ɵdir = /* @__PURE__ */i0.ɵɵdefineDirective({
        type: DefaultLayoutAlignDirective,
        selectors: [["", "fxLayoutAlign", ""], ["", "fxLayoutAlign.xs", ""], ["", "fxLayoutAlign.sm", ""], ["", "fxLayoutAlign.md", ""], ["", "fxLayoutAlign.lg", ""], ["", "fxLayoutAlign.xl", ""], ["", "fxLayoutAlign.lt-sm", ""], ["", "fxLayoutAlign.lt-md", ""], ["", "fxLayoutAlign.lt-lg", ""], ["", "fxLayoutAlign.lt-xl", ""], ["", "fxLayoutAlign.gt-xs", ""], ["", "fxLayoutAlign.gt-sm", ""], ["", "fxLayoutAlign.gt-md", ""], ["", "fxLayoutAlign.gt-lg", ""]],
        inputs: {
          fxLayoutAlign: "fxLayoutAlign",
          "fxLayoutAlign.xs": "fxLayoutAlign.xs",
          "fxLayoutAlign.sm": "fxLayoutAlign.sm",
          "fxLayoutAlign.md": "fxLayoutAlign.md",
          "fxLayoutAlign.lg": "fxLayoutAlign.lg",
          "fxLayoutAlign.xl": "fxLayoutAlign.xl",
          "fxLayoutAlign.lt-sm": "fxLayoutAlign.lt-sm",
          "fxLayoutAlign.lt-md": "fxLayoutAlign.lt-md",
          "fxLayoutAlign.lt-lg": "fxLayoutAlign.lt-lg",
          "fxLayoutAlign.lt-xl": "fxLayoutAlign.lt-xl",
          "fxLayoutAlign.gt-xs": "fxLayoutAlign.gt-xs",
          "fxLayoutAlign.gt-sm": "fxLayoutAlign.gt-sm",
          "fxLayoutAlign.gt-md": "fxLayoutAlign.gt-md",
          "fxLayoutAlign.gt-lg": "fxLayoutAlign.gt-lg"
        },
        features: [i0.ɵɵInheritDefinitionFeature]
      });
    }
  }
  return DefaultLayoutAlignDirective;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();
const layoutAlignHorizontalCache = new Map();
const layoutAlignVerticalCache = new Map();
const layoutAlignHorizontalRevCache = new Map();
const layoutAlignVerticalRevCache = new Map();
const layoutAlignHorizontalInlineCache = new Map();
const layoutAlignVerticalInlineCache = new Map();
const layoutAlignHorizontalRevInlineCache = new Map();
const layoutAlignVerticalRevInlineCache = new Map();
const CLEAR_MARGIN_CSS = {
  'margin-left': null,
  'margin-right': null,
  'margin-top': null,
  'margin-bottom': null
};
let LayoutGapStyleBuilder = /*#__PURE__*/(() => {
  class LayoutGapStyleBuilder extends StyleBuilder {
    constructor(_styler, _config) {
      super();
      this._styler = _styler;
      this._config = _config;
    }
    buildStyles(gapValue, parent) {
      if (gapValue.endsWith(GRID_SPECIFIER)) {
        gapValue = gapValue.slice(0, gapValue.indexOf(GRID_SPECIFIER));
        gapValue = _multiply(gapValue, this._config.multiplier);
        // Add the margin to the host element
        return buildGridMargin(gapValue, parent.directionality);
      } else {
        return {};
      }
    }
    sideEffect(gapValue, _styles, parent) {
      const items = parent.items;
      if (gapValue.endsWith(GRID_SPECIFIER)) {
        gapValue = gapValue.slice(0, gapValue.indexOf(GRID_SPECIFIER));
        gapValue = _multiply(gapValue, this._config.multiplier);
        // For each `element` children, set the padding
        const paddingStyles = buildGridPadding(gapValue, parent.directionality);
        this._styler.applyStyleToElements(paddingStyles, parent.items);
      } else {
        gapValue = _multiply(gapValue, this._config.multiplier);
        gapValue = this.addFallbackUnit(gapValue);
        const lastItem = items.pop();
        // For each `element` children EXCEPT the last,
        // set the margin right/bottom styles...
        const gapCss = buildGapCSS(gapValue, parent);
        this._styler.applyStyleToElements(gapCss, items);
        // Clear all gaps for all visible elements
        this._styler.applyStyleToElements(CLEAR_MARGIN_CSS, [lastItem]);
      }
    }
    addFallbackUnit(value) {
      return !isNaN(+value) ? `${value}${this._config.defaultUnit}` : value;
    }
    static {
      this.ɵfac = function LayoutGapStyleBuilder_Factory(__ngFactoryType__) {
        return new (__ngFactoryType__ || LayoutGapStyleBuilder)(i0.ɵɵinject(i1.StyleUtils), i0.ɵɵinject(LAYOUT_CONFIG));
      };
    }
    static {
      this.ɵprov = /* @__PURE__ */i0.ɵɵdefineInjectable({
        token: LayoutGapStyleBuilder,
        factory: LayoutGapStyleBuilder.ɵfac,
        providedIn: 'root'
      });
    }
  }
  return LayoutGapStyleBuilder;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();
const inputs$1 = ['fxLayoutGap', 'fxLayoutGap.xs', 'fxLayoutGap.sm', 'fxLayoutGap.md', 'fxLayoutGap.lg', 'fxLayoutGap.xl', 'fxLayoutGap.lt-sm', 'fxLayoutGap.lt-md', 'fxLayoutGap.lt-lg', 'fxLayoutGap.lt-xl', 'fxLayoutGap.gt-xs', 'fxLayoutGap.gt-sm', 'fxLayoutGap.gt-md', 'fxLayoutGap.gt-lg'];
const selector$1 = `
  [fxLayoutGap], [fxLayoutGap.xs], [fxLayoutGap.sm], [fxLayoutGap.md],
  [fxLayoutGap.lg], [fxLayoutGap.xl], [fxLayoutGap.lt-sm], [fxLayoutGap.lt-md],
  [fxLayoutGap.lt-lg], [fxLayoutGap.lt-xl], [fxLayoutGap.gt-xs], [fxLayoutGap.gt-sm],
  [fxLayoutGap.gt-md], [fxLayoutGap.gt-lg]
`;
/**
 * 'layout-padding' styling directive
 *  Defines padding of child elements in a layout container
 */
let LayoutGapDirective = /*#__PURE__*/(() => {
  class LayoutGapDirective extends BaseDirective2 {
    /** Special accessor to query for all child 'element' nodes regardless of type, class, etc */
    get childrenNodes() {
      const obj = this.nativeElement.children;
      const buffer = [];
      // iterate backwards ensuring that length is an UInt32
      for (let i = obj.length; i--;) {
        buffer[i] = obj[i];
      }
      return buffer;
    }
    constructor(elRef, zone, directionality, styleUtils, styleBuilder, marshal) {
      super(elRef, styleBuilder, styleUtils, marshal);
      this.zone = zone;
      this.directionality = directionality;
      this.styleUtils = styleUtils;
      this.layout = 'row'; // default flex-direction
      this.DIRECTIVE_KEY = 'layout-gap';
      this.observerSubject = new Subject();
      const extraTriggers = [this.directionality.change, this.observerSubject.asObservable()];
      this.init(extraTriggers);
      this.marshal.trackValue(this.nativeElement, 'layout').pipe(takeUntil(this.destroySubject)).subscribe(this.onLayoutChange.bind(this));
    }
    // *********************************************
    // Lifecycle Methods
    // *********************************************
    ngAfterContentInit() {
      this.buildChildObservable();
      this.triggerUpdate();
    }
    ngOnDestroy() {
      super.ngOnDestroy();
      if (this.observer) {
        this.observer.disconnect();
      }
    }
    // *********************************************
    // Protected methods
    // *********************************************
    /**
     * Cache the parent container 'flex-direction' and update the 'margin' styles
     */
    onLayoutChange(matcher) {
      const layout = matcher.value;
      // Make sure to filter out 'wrap' option
      let newDirection = layout.split(' ')[0];
      if (!LAYOUT_VALUES.find(x => x === newDirection)) {
        newDirection = 'row';
      }
      // Clear the previous style before we change the layout
      if (this.layout && this.layout !== newDirection) {
        this.clearStyles();
      }
      this.layout = newDirection;
      this.triggerUpdate();
    }
    /**
     *
     */
    updateWithValue(value) {
      // Gather all non-hidden Element nodes
      const items = this.childrenNodes.filter(el => el.nodeType === 1 && this.willDisplay(el)).sort((a, b) => {
        const orderA = +this.styler.lookupStyle(a, 'order');
        const orderB = +this.styler.lookupStyle(b, 'order');
        if (isNaN(orderA) || isNaN(orderB) || orderA === orderB) {
          return 0;
        } else {
          return orderA > orderB ? 1 : -1;
        }
      });
      if (items.length > 0) {
        const directionality = this.directionality.value;
        const layout = this.layout;
        if (layout === 'row' && directionality === 'rtl') {
          this.styleCache = layoutGapCacheRowRtl;
        } else if (layout === 'row' && directionality !== 'rtl') {
          this.styleCache = layoutGapCacheRowLtr;
        } else if (layout === 'column' && directionality === 'rtl') {
          this.styleCache = layoutGapCacheColumnRtl;
        } else if (layout === 'column' && directionality !== 'rtl') {
          this.styleCache = layoutGapCacheColumnLtr;
        }
        this.addStyles(value, {
          directionality,
          items,
          layout
        });
      }
    }
    /** We need to override clearStyles because in most cases mru isn't populated */
    clearStyles() {
      const gridMode = Object.keys(this.mru).length > 0;
      const childrenStyle = gridMode ? 'padding' : getMarginType(this.directionality.value, this.layout);
      // If there are styles on the parent remove them
      if (gridMode) {
        super.clearStyles();
      }
      // Then remove the children styles too
      this.styleUtils.applyStyleToElements({
        [childrenStyle]: ''
      }, this.childrenNodes);
    }
    /** Determine if an element will show or hide based on current activation */
    willDisplay(source) {
      const value = this.marshal.getValue(source, 'show-hide');
      return value === true || value === undefined && this.styleUtils.lookupStyle(source, 'display') !== 'none';
    }
    buildChildObservable() {
      this.zone.runOutsideAngular(() => {
        if (typeof MutationObserver !== 'undefined') {
          this.observer = new MutationObserver(mutations => {
            const validatedChanges = it => {
              return it.addedNodes && it.addedNodes.length > 0 || it.removedNodes && it.removedNodes.length > 0;
            };
            // update gap styles only for child 'added' or 'removed' events
            if (mutations.some(validatedChanges)) {
              this.observerSubject.next();
            }
          });
          this.observer.observe(this.nativeElement, {
            childList: true
          });
        }
      });
    }
    static {
      this.ɵfac = function LayoutGapDirective_Factory(__ngFactoryType__) {
        return new (__ngFactoryType__ || LayoutGapDirective)(i0.ɵɵdirectiveInject(i0.ElementRef), i0.ɵɵdirectiveInject(i0.NgZone), i0.ɵɵdirectiveInject(i1$1.Directionality), i0.ɵɵdirectiveInject(i1.StyleUtils), i0.ɵɵdirectiveInject(LayoutGapStyleBuilder), i0.ɵɵdirectiveInject(i1.MediaMarshaller));
      };
    }
    static {
      this.ɵdir = /* @__PURE__ */i0.ɵɵdefineDirective({
        type: LayoutGapDirective,
        features: [i0.ɵɵInheritDefinitionFeature]
      });
    }
  }
  return LayoutGapDirective;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();
let DefaultLayoutGapDirective = /*#__PURE__*/(() => {
  class DefaultLayoutGapDirective extends LayoutGapDirective {
    constructor() {
      super(...arguments);
      this.inputs = inputs$1;
    }
    static {
      this.ɵfac = /* @__PURE__ */(() => {
        let ɵDefaultLayoutGapDirective_BaseFactory;
        return function DefaultLayoutGapDirective_Factory(__ngFactoryType__) {
          return (ɵDefaultLayoutGapDirective_BaseFactory || (ɵDefaultLayoutGapDirective_BaseFactory = i0.ɵɵgetInheritedFactory(DefaultLayoutGapDirective)))(__ngFactoryType__ || DefaultLayoutGapDirective);
        };
      })();
    }
    static {
      this.ɵdir = /* @__PURE__ */i0.ɵɵdefineDirective({
        type: DefaultLayoutGapDirective,
        selectors: [["", "fxLayoutGap", ""], ["", "fxLayoutGap.xs", ""], ["", "fxLayoutGap.sm", ""], ["", "fxLayoutGap.md", ""], ["", "fxLayoutGap.lg", ""], ["", "fxLayoutGap.xl", ""], ["", "fxLayoutGap.lt-sm", ""], ["", "fxLayoutGap.lt-md", ""], ["", "fxLayoutGap.lt-lg", ""], ["", "fxLayoutGap.lt-xl", ""], ["", "fxLayoutGap.gt-xs", ""], ["", "fxLayoutGap.gt-sm", ""], ["", "fxLayoutGap.gt-md", ""], ["", "fxLayoutGap.gt-lg", ""]],
        inputs: {
          fxLayoutGap: "fxLayoutGap",
          "fxLayoutGap.xs": "fxLayoutGap.xs",
          "fxLayoutGap.sm": "fxLayoutGap.sm",
          "fxLayoutGap.md": "fxLayoutGap.md",
          "fxLayoutGap.lg": "fxLayoutGap.lg",
          "fxLayoutGap.xl": "fxLayoutGap.xl",
          "fxLayoutGap.lt-sm": "fxLayoutGap.lt-sm",
          "fxLayoutGap.lt-md": "fxLayoutGap.lt-md",
          "fxLayoutGap.lt-lg": "fxLayoutGap.lt-lg",
          "fxLayoutGap.lt-xl": "fxLayoutGap.lt-xl",
          "fxLayoutGap.gt-xs": "fxLayoutGap.gt-xs",
          "fxLayoutGap.gt-sm": "fxLayoutGap.gt-sm",
          "fxLayoutGap.gt-md": "fxLayoutGap.gt-md",
          "fxLayoutGap.gt-lg": "fxLayoutGap.gt-lg"
        },
        features: [i0.ɵɵInheritDefinitionFeature]
      });
    }
  }
  return DefaultLayoutGapDirective;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();
const layoutGapCacheRowRtl = new Map();
const layoutGapCacheColumnRtl = new Map();
const layoutGapCacheRowLtr = new Map();
const layoutGapCacheColumnLtr = new Map();
const GRID_SPECIFIER = ' grid';
function buildGridPadding(value, directionality) {
  const [between, below] = value.split(' ');
  const bottom = below ?? between;
  let paddingRight = '0px',
    paddingBottom = bottom,
    paddingLeft = '0px';
  if (directionality === 'rtl') {
    paddingLeft = between;
  } else {
    paddingRight = between;
  }
  return {
    padding: `0px ${paddingRight} ${paddingBottom} ${paddingLeft}`
  };
}
function buildGridMargin(value, directionality) {
  const [between, below] = value.split(' ');
  const bottom = below ?? between;
  const minus = str => `-${str}`;
  let marginRight = '0px',
    marginBottom = minus(bottom),
    marginLeft = '0px';
  if (directionality === 'rtl') {
    marginLeft = minus(between);
  } else {
    marginRight = minus(between);
  }
  return {
    margin: `0px ${marginRight} ${marginBottom} ${marginLeft}`
  };
}
function getMarginType(directionality, layout) {
  switch (layout) {
    case 'column':
      return 'margin-bottom';
    case 'column-reverse':
      return 'margin-top';
    case 'row':
      return directionality === 'rtl' ? 'margin-left' : 'margin-right';
    case 'row-reverse':
      return directionality === 'rtl' ? 'margin-right' : 'margin-left';
    default:
      return directionality === 'rtl' ? 'margin-left' : 'margin-right';
  }
}
function buildGapCSS(gapValue, parent) {
  const key = getMarginType(parent.directionality, parent.layout);
  const margins = {
    ...CLEAR_MARGIN_CSS
  };
  margins[key] = gapValue;
  return margins;
}

/**
 * @license
 * Copyright Google LLC All Rights Reserved.
 *
 * Use of this source code is governed by an MIT-style license that can be
 * found in the LICENSE file at https://angular.io/license
 */
let LayoutStyleBuilder = /*#__PURE__*/(() => {
  class LayoutStyleBuilder extends StyleBuilder {
    buildStyles(input, {
      display
    }) {
      const css = buildLayoutCSS(input);
      return {
        ...css,
        display: display === 'none' ? display : css.display
      };
    }
    static {
      this.ɵfac = /* @__PURE__ */(() => {
        let ɵLayoutStyleBuilder_BaseFactory;
        return function LayoutStyleBuilder_Factory(__ngFactoryType__) {
          return (ɵLayoutStyleBuilder_BaseFactory || (ɵLayoutStyleBuilder_BaseFactory = i0.ɵɵgetInheritedFactory(LayoutStyleBuilder)))(__ngFactoryType__ || LayoutStyleBuilder);
        };
      })();
    }
    static {
      this.ɵprov = /* @__PURE__ */i0.ɵɵdefineInjectable({
        token: LayoutStyleBuilder,
        factory: LayoutStyleBuilder.ɵfac,
        providedIn: 'root'
      });
    }
  }
  return LayoutStyleBuilder;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();
const inputs = ['fxLayout', 'fxLayout.xs', 'fxLayout.sm', 'fxLayout.md', 'fxLayout.lg', 'fxLayout.xl', 'fxLayout.lt-sm', 'fxLayout.lt-md', 'fxLayout.lt-lg', 'fxLayout.lt-xl', 'fxLayout.gt-xs', 'fxLayout.gt-sm', 'fxLayout.gt-md', 'fxLayout.gt-lg'];
const selector = `
  [fxLayout], [fxLayout.xs], [fxLayout.sm], [fxLayout.md],
  [fxLayout.lg], [fxLayout.xl], [fxLayout.lt-sm], [fxLayout.lt-md],
  [fxLayout.lt-lg], [fxLayout.lt-xl], [fxLayout.gt-xs], [fxLayout.gt-sm],
  [fxLayout.gt-md], [fxLayout.gt-lg]
`;
/**
 * 'layout' flexbox styling directive
 * Defines the positioning flow direction for the child elements: row or column
 * Optional values: column or row (default)
 * @see https://css-tricks.com/almanac/properties/f/flex-direction/
 *
 */
let LayoutDirective = /*#__PURE__*/(() => {
  class LayoutDirective extends BaseDirective2 {
    constructor(elRef, styleUtils, styleBuilder, marshal, _config) {
      super(elRef, styleBuilder, styleUtils, marshal);
      this._config = _config;
      this.DIRECTIVE_KEY = 'layout';
      this.init();
    }
    updateWithValue(input) {
      const detectLayoutDisplay = this._config.detectLayoutDisplay;
      const display = detectLayoutDisplay ? this.styler.lookupStyle(this.nativeElement, 'display') : '';
      this.styleCache = cacheMap.get(display) ?? new Map();
      cacheMap.set(display, this.styleCache);
      if (this.currentValue !== input) {
        this.addStyles(input, {
          display
        });
        this.currentValue = input;
      }
    }
    static {
      this.ɵfac = function LayoutDirective_Factory(__ngFactoryType__) {
        return new (__ngFactoryType__ || LayoutDirective)(i0.ɵɵdirectiveInject(i0.ElementRef), i0.ɵɵdirectiveInject(i1.StyleUtils), i0.ɵɵdirectiveInject(LayoutStyleBuilder), i0.ɵɵdirectiveInject(i1.MediaMarshaller), i0.ɵɵdirectiveInject(LAYOUT_CONFIG));
      };
    }
    static {
      this.ɵdir = /* @__PURE__ */i0.ɵɵdefineDirective({
        type: LayoutDirective,
        features: [i0.ɵɵInheritDefinitionFeature]
      });
    }
  }
  return LayoutDirective;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();
let DefaultLayoutDirective = /*#__PURE__*/(() => {
  class DefaultLayoutDirective extends LayoutDirective {
    constructor() {
      super(...arguments);
      this.inputs = inputs;
    }
    static {
      this.ɵfac = /* @__PURE__ */(() => {
        let ɵDefaultLayoutDirective_BaseFactory;
        return function DefaultLayoutDirective_Factory(__ngFactoryType__) {
          return (ɵDefaultLayoutDirective_BaseFactory || (ɵDefaultLayoutDirective_BaseFactory = i0.ɵɵgetInheritedFactory(DefaultLayoutDirective)))(__ngFactoryType__ || DefaultLayoutDirective);
        };
      })();
    }
    static {
      this.ɵdir = /* @__PURE__ */i0.ɵɵdefineDirective({
        type: DefaultLayoutDirective,
        selectors: [["", "fxLayout", ""], ["", "fxLayout.xs", ""], ["", "fxLayout.sm", ""], ["", "fxLayout.md", ""], ["", "fxLayout.lg", ""], ["", "fxLayout.xl", ""], ["", "fxLayout.lt-sm", ""], ["", "fxLayout.lt-md", ""], ["", "fxLayout.lt-lg", ""], ["", "fxLayout.lt-xl", ""], ["", "fxLayout.gt-xs", ""], ["", "fxLayout.gt-sm", ""], ["", "fxLayout.gt-md", ""], ["", "fxLayout.gt-lg", ""]],
        inputs: {
          fxLayout: "fxLayout",
          "fxLayout.xs": "fxLayout.xs",
          "fxLayout.sm": "fxLayout.sm",
          "fxLayout.md": "fxLayout.md",
          "fxLayout.lg": "fxLayout.lg",
          "fxLayout.xl": "fxLayout.xl",
          "fxLayout.lt-sm": "fxLayout.lt-sm",
          "fxLayout.lt-md": "fxLayout.lt-md",
          "fxLayout.lt-lg": "fxLayout.lt-lg",
          "fxLayout.lt-xl": "fxLayout.lt-xl",
          "fxLayout.gt-xs": "fxLayout.gt-xs",
          "fxLayout.gt-sm": "fxLayout.gt-sm",
          "fxLayout.gt-md": "fxLayout.gt-md",
          "fxLayout.gt-lg": "fxLayout.gt-lg"
        },
        features: [i0.ɵɵInheritDefinitionFeature]
      });
    }
  }
  return DefaultLayoutDirective;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();
const cacheMap = new Map();

/**
 * @license
 * Copyright Google LLC All Rights Reserved.
 *
 * Use of this source code is governed by an MIT-style license that can be
 * found in the LICENSE file at https://angular.io/license
 */
const ALL_DIRECTIVES = [DefaultLayoutDirective, DefaultLayoutGapDirective, DefaultLayoutAlignDirective, DefaultFlexOrderDirective, DefaultFlexOffsetDirective, FlexFillDirective, DefaultFlexAlignDirective, DefaultFlexDirective];
/**
 * *****************************************************************
 * Define module for the Flex API
 * *****************************************************************
 */
let FlexModule = /*#__PURE__*/(() => {
  class FlexModule {
    static {
      this.ɵfac = function FlexModule_Factory(__ngFactoryType__) {
        return new (__ngFactoryType__ || FlexModule)();
      };
    }
    static {
      this.ɵmod = /* @__PURE__ */i0.ɵɵdefineNgModule({
        type: FlexModule
      });
    }
    static {
      this.ɵinj = /* @__PURE__ */i0.ɵɵdefineInjector({
        imports: [CoreModule, BidiModule]
      });
    }
  }
  return FlexModule;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();

/**
 * @license
 * Copyright Google LLC All Rights Reserved.
 *
 * Use of this source code is governed by an MIT-style license that can be
 * found in the LICENSE file at https://angular.io/license
 */

/**
 * Generated bundle index. Do not edit.
 */

export { DefaultFlexAlignDirective, DefaultFlexDirective, DefaultFlexOffsetDirective, DefaultFlexOrderDirective, DefaultLayoutAlignDirective, DefaultLayoutDirective, DefaultLayoutGapDirective, FlexAlignDirective, FlexAlignStyleBuilder, FlexDirective, FlexFillDirective, FlexFillStyleBuilder, FlexModule, FlexOffsetDirective, FlexOffsetStyleBuilder, FlexOrderDirective, FlexOrderStyleBuilder, FlexStyleBuilder, LayoutAlignDirective, LayoutAlignStyleBuilder, LayoutDirective, LayoutGapDirective, LayoutGapStyleBuilder, LayoutStyleBuilder };
